import { ComplaintModal } from "@complaints/ComplaintModal";
import {
  GO_TO_SHORTCUTS,
  type GoToTarget,
} from "@components/keyboardShortcuts";
import { KeyboardShortcutsModal } from "@components/KeyboardShortcutsModal";
import { TalkToBrianButton } from "@components/TalkToBrianButton";
import { useCompanionShortcuts } from "@components/useCompanionShortcuts";
import { UserMenu } from "@components/UserMenu";
import { useUser } from "@context/UserContext";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@scribenote/brian-design-system";
import { SubjectSearchModal } from "@subjects/SubjectSearchModal";
import { useNavigate } from "@tanstack/react-router";
import { FailedUploadsBanner } from "@transcription/FailedUploadsBanner";
import { canAccessAdmin } from "@users/userClient";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";
import { useCallback, useEffect, useState } from "react";

/**
 * Companion's global chrome: Talk to Brian, failed uploads, calendar and the user
 * menu, as one 64px row of 40px icon buttons.
 *
 * Mounts on every authenticated route — directly in the workspace's rail column,
 * via `CompanionHeader` elsewhere — so it also owns the global keyboard shortcuts
 * and the overlays they open, keeping the key handler beside the state it sets.
 *
 * Buttons are `shrink-0` and the user menu `min-w-0`/truncating because the row is
 * bounded by the 360px rail column; otherwise a long name ovalises the circles.
 * Talk to Brian is here rather than in a route because its session belongs to the
 * router root, so navigating never interrupts a live recording.
 *
 * The cluster always sits on the desk: directly on it in the workspace, on the desk
 * band `CompanionHeader` draws elsewhere. So it has one treatment, paper ink on
 * espresso with a faint paper tint on hover and the desk accent for focus, and
 * nothing here reads a theme.
 */
export const CompanionControls = () => {
  const navigate = useNavigate();
  const { currentUser, switchUser } = useUser();
  const { goHome } = useWorkspaceNavigation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);

  const goTo = useCallback(
    (target: GoToTarget) => {
      // Admin-only targets are silently ignored for users without access, like
      // the hidden menu item.
      const shortcut = GO_TO_SHORTCUTS.find((s) => s.target === target);
      if (shortcut?.adminOnly && !canAccessAdmin(currentUser)) return;
      switch (target) {
        case "home":
          goHome();
          break;
        case "calendar":
          navigate({ to: "/calendar" });
          break;
        case "tasks":
          navigate({ to: "/tasks" });
          break;
        case "admin":
          navigate({ to: "/admin" });
          break;
      }
    },
    [currentUser, goHome, navigate],
  );

  useCompanionShortcuts({
    onSearch: () => setSearchOpen(true),
    onHelp: () => setShortcutsOpen(true),
    onGoTo: goTo,
    onSwitchUser: switchUser,
  });

  useEffect(() => {
    // TODO(BE-1951): TEMPORARY — remove this effect with src/complaints/.
    // Ctrl/⌘+Shift+H — "report a problem" hotline (WEB-3201). Shift is
    // load-bearing on Mac: plain ⌘H is the OS-level hide-app shortcut and
    // never reaches the page. With Shift held, `key` reports "H".
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.key.toLowerCase() === "h" &&
        e.shiftKey &&
        (e.metaKey || e.ctrlKey)
      ) {
        e.preventDefault();
        setComplaintOpen(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex h-16 min-w-0 shrink-0 items-center justify-end font-body">
      <TalkToBrianButton />
      <FailedUploadsBanner />

      <button
        type="button"
        onClick={() => navigate({ to: "/calendar" })}
        aria-label="Calendar"
        title="Calendar"
        data-dd-action-name="Calendar"
        data-testid="header-calendar-nav"
        className={cn(
          "flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-inverse",
          "transition-colors duration-150 ease-paper motion-reduce:transition-none",
          "hover:bg-paper/10",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite",
        )}
      >
        <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
      </button>
      <SubjectSearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
      <ComplaintModal
        isOpen={complaintOpen}
        onClose={() => setComplaintOpen(false)}
      />
      <KeyboardShortcutsModal
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
      {/* The menu keeps the flag in its interface (its paper treatment is the other
          branch, and this file does not own it), so pass the constant. */}
      <UserMenu
        onDarkBackdrop
        onOpenSearch={() => setSearchOpen(true)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
      />
    </div>
  );
};
